import { ArrowLeft, Package, Save, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { showToast } from "../../lib/toast";
import { formatBusinessDate } from "../../lib/utils";
import { ProductPack, productService } from "../../services/product.service";
import {
  PurchaseOrderItemDto,
  purchaseOrderService,
  UpdatePurchaseOrderDto,
} from "../../services/purchase-order.service";
import {
  SupplierProductPrice,
  supplierProductPriceService,
} from "../../services/supplier-product-price.service";
import { Supplier, supplierService } from "../../services/supplier.service";

interface ProductSearchResult {
  id: string;
  name: string;
  sku: string;
  unit: string;
  manufacturer?: string;
  hsnCode?: string;
  purchasePrice: number;
  sellingPrice?: number;
  mrp?: number;
  gstPercent?: number;
}

interface PurchaseOrderItemWithCalculations extends PurchaseOrderItemDto {
  id?: string;
  productName: string;
  sku: string;
  unit: string;
  discountAmount: number;
  schemeDiscountAmount: number;
  taxableAmount: number;
  taxAmount: number;
  totalAmount: number;
  selectedPackId: string;
  baseQuantity: number;
}

export function EditPurchaseOrderPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  const [selectedSupplier, setSelectedSupplier] = useState<string>("");
  const [poDate, setPoDate] = useState("");
  const [expectedDelivery, setExpectedDelivery] = useState("");
  const [notes, setNotes] = useState("");
  const [termsConditions, setTermsConditions] = useState("");
  const [items, setItems] = useState<PurchaseOrderItemWithCalculations[]>([]);
  const [poStatus, setPoStatus] = useState<string>("");

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<ProductSearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const [productMap, setProductMap] = useState<
    Map<string, ProductSearchResult>
  >(new Map());
  const [packsMap, setPacksMap] = useState<Map<string, ProductPack[]>>(
    new Map(),
  );
  const [supplierPriceMap, setSupplierPriceMap] = useState<
    Map<string, SupplierProductPrice>
  >(new Map());

  const roundCurrency = (value: number) => Number(value.toFixed(2));

  const getPackBaseUnits = (
    packId: string | undefined,
    packs: ProductPack[],
    visited = new Set<string>(),
  ): number => {
    if (!packId) return 1;
    if (visited.has(packId)) return 1;
    visited.add(packId);

    const pack = packs.find((entry) => entry.id === packId);
    if (!pack) return 1;

    if (!pack.parentPackId) {
      return pack.unitsPerPack || 1;
    }

    return (
      (pack.unitsPerPack || 1) *
      getPackBaseUnits(pack.parentPackId || undefined, packs, visited)
    );
  };

  const getSupplierPriceMeta = (
    productId: string,
    unitPrice: number,
    unitsPerPack: number,
  ) => {
    const contract = supplierPriceMap.get(productId);
    if (!contract || contract.baseUnitPrice <= 0) {
      return null;
    }

    const contractedPackPrice = roundCurrency(
      contract.baseUnitPrice * Math.max(1, unitsPerPack),
    );
    const orderedBaseUnitPrice = roundCurrency(
      unitPrice / Math.max(1, unitsPerPack),
    );
    const deviationPercent = roundCurrency(
      ((orderedBaseUnitPrice - contract.baseUnitPrice) /
        contract.baseUnitPrice) *
        100,
    );

    return {
      contractedPackPrice,
      deviationPercent,
      isAlert: deviationPercent > contract.allowedDeviationPercent,
    };
  };

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (selectedSupplier) {
      loadSupplierProductPrices(selectedSupplier);
    } else {
      setSupplierPriceMap(new Map());
    }
  }, [selectedSupplier]);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchProducts();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const loadPacksForProducts = async (productIds: string[]) => {
    const uniqueIds = Array.from(new Set(productIds)).filter(Boolean);
    if (uniqueIds.length === 0) return;

    const packEntries = await Promise.all(
      uniqueIds.map(async (productId) => {
        try {
          const packs = await productService.getProductPacks(productId);
          return [productId, packs] as [string, ProductPack[]];
        } catch {
          return [productId, []] as [string, ProductPack[]];
        }
      }),
    );

    setPacksMap((prev) => {
      const next = new Map(prev);
      packEntries.forEach(([productId, packs]) => next.set(productId, packs));
      return next;
    });
  };

  const loadSupplierProductPrices = async (supplierId: string) => {
    try {
      const contracts = await supplierProductPriceService.list({ supplierId });
      setSupplierPriceMap(
        new Map(contracts.map((contract) => [contract.productId, contract])),
      );
    } catch {
      setSupplierPriceMap(new Map());
    }
  };

  const calculateItemAmounts = (item: PurchaseOrderItemWithCalculations) => {
    const baseQuantity = item.quantity * (item.unitsPerPack || 1);
    item.baseQuantity = baseQuantity;

    // unitPrice is per pack, so use item.quantity (pack qty) not baseQuantity
    const subtotal = item.quantity * item.unitPrice;
    item.discountAmount = (subtotal * (item.discountPercent || 0)) / 100;
    const afterTradeDiscount = subtotal - item.discountAmount;

    item.schemeDiscountAmount =
      (afterTradeDiscount * (item.schemeDiscountPercent || 0)) / 100;
    item.taxableAmount = afterTradeDiscount - item.schemeDiscountAmount;
    item.taxAmount = (item.taxableAmount * (item.taxPercent || 0)) / 100;
    item.totalAmount = item.taxableAmount + item.taxAmount;
  };

  const loadData = async () => {
    if (!id) {
      showToast.error("No purchase order ID provided");
      navigate("/purchase-orders");
      return;
    }

    try {
      setLoading(true);

      const [po, suppliersData] = await Promise.all([
        purchaseOrderService.getPurchaseOrder(id),
        supplierService.getSuppliers({ isActive: true, limit: 100 }),
      ]);

      setSuppliers(suppliersData.data || []);
      setSelectedSupplier(po.supplierId);
      setPoDate(formatBusinessDate(po.poDate));
      setExpectedDelivery(
        po.expectedDelivery ? po.expectedDelivery.split("T")[0] : "",
      );
      setNotes(po.notes || "");
      setTermsConditions(po.termsConditions || "");
      setPoStatus(po.status);

      const nextProductMap = new Map<string, ProductSearchResult>();

      const mappedItems: PurchaseOrderItemWithCalculations[] = (
        po.items || []
      ).map((item) => {
        const unitsPerPack = Number(item.unitsPerPack || 1);
        const product = {
          id: item.productId,
          name: item.product?.name || "Unknown Product",
          sku: item.product?.sku || "",
          unit: item.product?.unit || "pcs",
          manufacturer: item.product?.manufacturer || "",
          hsnCode: item.product?.hsnCode || undefined,
          purchasePrice: Number(item.unitPrice || 0),
          mrp: Number(item.mrp || item.product?.mrp || 0),
          gstPercent: Number(item.taxPercent || 0),
        };
        nextProductMap.set(item.productId, product);

        const mapped: PurchaseOrderItemWithCalculations = {
          id: item.id,
          productId: item.productId,
          productName: product.name,
          sku: product.sku,
          unit: product.unit,
          quantity: Number(item.quantity),
          freeQuantity: Number(item.freeQuantity || 0),
          unitPrice: Number(item.unitPrice),
          mrp: Number(item.mrp || item.product?.mrp || 0),
          discountPercent: Number(item.discountPercent || 0),
          discountAmount: Number(item.discountAmount || 0),
          schemeDiscountPercent: Number(item.schemeDiscountPercent || 0),
          schemeDiscountAmount: Number(item.schemeDiscountAmount || 0),
          taxableAmount: Number(item.taxableAmount || 0),
          taxPercent: Number(item.taxPercent || 0),
          taxAmount: Number(item.taxAmount || 0),
          totalAmount: Number(item.totalAmount || 0),
          purchasePackId: item.purchasePackId,
          unitsPerPack,
          selectedPackId: item.purchasePackId || "",
          baseQuantity: Number(item.quantity) * unitsPerPack,
        };
        calculateItemAmounts(mapped);
        return mapped;
      });

      setProductMap(nextProductMap);
      setItems(mappedItems);
      await loadPacksForProducts(mappedItems.map((item) => item.productId));
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to load purchase order",
      );
      navigate("/purchase-orders");
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = async () => {
    try {
      const response = await productService.getProducts({
        search: searchTerm,
        limit: 20,
      });
      setSearchResults(response.data || []);
      setShowSearchResults(true);
    } catch {
      setSearchResults([]);
    }
  };

  const addProductToOrder = async (product: ProductSearchResult) => {
    const exists = items.find((item) => item.productId === product.id);
    if (exists) {
      showToast.error("Product already added");
      return;
    }

    setProductMap((prev) => new Map(prev).set(product.id, product));

    let packs: ProductPack[] = [];
    try {
      packs = await productService.getProductPacks(product.id);
      setPacksMap((prev) => new Map(prev).set(product.id, packs));
    } catch {
      setPacksMap((prev) => new Map(prev).set(product.id, []));
    }

    const defaultPack =
      packs.find((p) => p.isPurchaseUnit && p.isDefault) ||
      packs.find((p) => p.isPurchaseUnit) ||
      packs.find((p) => p.isDefault) ||
      null;

    const unitsPerPack = defaultPack
      ? getPackBaseUnits(defaultPack.id, packs)
      : 1;
    const contract = supplierPriceMap.get(product.id);

    const newItem: PurchaseOrderItemWithCalculations = {
      productId: product.id,
      productName: product.name,
      sku: product.sku,
      unit: product.unit,
      quantity: 1,
      freeQuantity: 0,
      unitPrice: contract
        ? roundCurrency(contract.baseUnitPrice * unitsPerPack)
        : defaultPack
          ? Number(defaultPack.purchasePrice)
          : product.purchasePrice || 0,
      mrp: defaultPack ? Number(defaultPack.mrp) : Number(product.mrp || 0),
      discountPercent: 0,
      discountAmount: 0,
      schemeDiscountPercent: 0,
      schemeDiscountAmount: 0,
      taxableAmount: 0,
      taxPercent: Number(product.gstPercent || 0),
      taxAmount: 0,
      totalAmount: 0,
      purchasePackId: defaultPack?.id,
      unitsPerPack,
      selectedPackId: defaultPack?.id || "",
      baseQuantity: unitsPerPack,
    };

    calculateItemAmounts(newItem);
    setItems((prev) => [...prev, newItem]);
    setSearchTerm("");
    setShowSearchResults(false);
  };

  const updateItem = (
    index: number,
    field: keyof PurchaseOrderItemDto | "selectedPackId" | "manualUnitsPerPack",
    value: any,
  ) => {
    const updatedItems = [...items];
    const item = { ...updatedItems[index] };

    if (field === "selectedPackId") {
      const packs = packsMap.get(item.productId) || [];
      if (value === "") {
        item.selectedPackId = "";
        item.purchasePackId = undefined;
        item.unitsPerPack = 1;
        const product = productMap.get(item.productId);
        const contract = supplierPriceMap.get(item.productId);
        item.unitPrice = contract
          ? roundCurrency(contract.baseUnitPrice)
          : product?.purchasePrice || item.unitPrice;
        item.mrp = product?.mrp || item.mrp;
      } else {
        const pack = packs.find((p) => p.id === value);
        if (pack) {
          const contract = supplierPriceMap.get(item.productId);
          const flattenedUnitsPerPack = getPackBaseUnits(pack.id, packs);
          item.selectedPackId = pack.id;
          item.purchasePackId = pack.id;
          item.unitsPerPack = flattenedUnitsPerPack;
          item.unitPrice = contract
            ? roundCurrency(contract.baseUnitPrice * flattenedUnitsPerPack)
            : Number(pack.purchasePrice);
          item.mrp = Number(pack.mrp);
        }
      }
    } else if (field === "manualUnitsPerPack") {
      const upp = Math.max(1, parseInt(value) || 1);
      const contract = supplierPriceMap.get(item.productId);
      item.unitsPerPack = upp;
      item.purchasePackId = undefined;
      item.selectedPackId = "";
      if (contract) {
        item.unitPrice = roundCurrency(contract.baseUnitPrice * upp);
      }
    } else {
      (item as any)[field] = value;
    }

    calculateItemAmounts(item);
    updatedItems[index] = item;
    setItems(updatedItems);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => {
      return sum + item.quantity * item.unitPrice;
    }, 0);
    const tradeDiscountAmount = items.reduce(
      (sum, item) => sum + item.discountAmount,
      0,
    );
    const schemeDiscountAmount = items.reduce(
      (sum, item) => sum + (item.schemeDiscountAmount || 0),
      0,
    );
    const discountAmount = tradeDiscountAmount + schemeDiscountAmount;
    const taxableAmount = items.reduce(
      (sum, item) => sum + (item.taxableAmount || 0),
      0,
    );
    const taxAmount = items.reduce((sum, item) => sum + item.taxAmount, 0);
    const totalAmount = items.reduce((sum, item) => sum + item.totalAmount, 0);

    return {
      subtotal,
      tradeDiscountAmount,
      schemeDiscountAmount,
      discountAmount,
      taxableAmount,
      taxAmount,
      totalAmount,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (items.length === 0) {
      showToast.error("Please add at least one product");
      return;
    }

    const payload: UpdatePurchaseOrderDto = {
      poDate: new Date(poDate).toISOString(),
      expectedDelivery: expectedDelivery
        ? new Date(expectedDelivery).toISOString()
        : undefined,
      notes: notes || undefined,
      termsConditions: termsConditions || undefined,
      items: items.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        freeQuantity: item.freeQuantity || 0,
        unitPrice: item.unitPrice,
        mrp: item.mrp || undefined,
        discountPercent: item.discountPercent,
        schemeDiscountPercent: item.schemeDiscountPercent || undefined,
        taxPercent: item.taxPercent,
        purchasePackId: item.purchasePackId || undefined,
        unitsPerPack: item.unitsPerPack || undefined,
      })),
    };

    try {
      setSaving(true);
      await purchaseOrderService.updatePurchaseOrder(id!, payload);
      showToast.success("Purchase order updated successfully");
      navigate("/purchase-orders");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to update purchase order",
      );
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto !text-gray-900">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate("/purchase-orders")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Edit Purchase Order
          </h1>
        </div>

        {poStatus !== "DRAFT" && (
          <div className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg text-sm font-medium">
            PO is {poStatus}. Editing might be restricted.
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Order Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier
              </label>
              <select
                value={selectedSupplier}
                disabled
                className="w-full px-4 py-2 border rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
              >
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PO Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={poDate}
                onChange={(e) => setPoDate(e.target.value)}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expected Delivery Date
              </label>
              <input
                type="date"
                value={expectedDelivery}
                onChange={(e) => setExpectedDelivery(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Products</h2>
          {supplierPriceMap.size > 0 && (
            <p className="mb-4 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              Contracted supplier rates loaded for {supplierPriceMap.size}{" "}
              product
              {supplierPriceMap.size === 1 ? "" : "s"}. Deviations above the
              configured threshold are flagged inline.
            </p>
          )}

          <div className="mb-6 relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search & Add Products
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by product name, SKU, or barcode..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {searchResults.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => addProductToOrder(product)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">
                      {product.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      SKU: {product.sku} | Unit: {product.unit} | Price: ₹
                      {product.purchasePrice}
                    </div>
                    {product.manufacturer && (
                      <div className="text-xs text-gray-400">
                        {product.manufacturer}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Product
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Pack
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      MRP
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Qty
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Free
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Base Qty
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Rate
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Trade %
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Scheme %
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Taxable
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      GST %
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Total
                    </th>
                    <th className="px-2 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((item, index) => {
                    const product = productMap.get(item.productId);
                    const packs = packsMap.get(item.productId) || [];
                    const supplierPriceMeta = getSupplierPriceMeta(
                      item.productId,
                      item.unitPrice,
                      item.unitsPerPack || 1,
                    );
                    return (
                      <tr key={index}>
                        <td className="px-3 py-3">
                          <div className="font-medium text-gray-900">
                            {product?.name ||
                              item.productName ||
                              "Unknown Product"}
                          </div>
                          <div className="text-xs text-gray-400">
                            {product?.sku || item.sku}
                            {product?.hsnCode && (
                              <span className="ml-2 text-gray-500">
                                HSN: {product.hsnCode}
                              </span>
                            )}
                          </div>
                          {product?.manufacturer && (
                            <div className="text-xs text-blue-500">
                              {product.manufacturer}
                            </div>
                          )}
                        </td>

                        <td className="px-2 py-3">
                          {packs.length > 0 ? (
                            <select
                              value={item.selectedPackId || ""}
                              onChange={(e) =>
                                updateItem(
                                  index,
                                  "selectedPackId",
                                  e.target.value,
                                )
                              }
                              className="w-32 px-2 py-1 border rounded text-sm"
                            >
                              <option value="">
                                Base ({product?.unit || item.unit || "unit"})
                              </option>
                              {packs.map((pack) => (
                                <option key={pack.id} value={pack.id}>
                                  {pack.packDescription} (
                                  {getPackBaseUnits(pack.id, packs)}{" "}
                                  {product?.unit || item.unit || "units"})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min="1"
                                value={item.unitsPerPack || 1}
                                onChange={(e) =>
                                  updateItem(
                                    index,
                                    "manualUnitsPerPack",
                                    e.target.value,
                                  )
                                }
                                className="w-14 px-1 py-1 border rounded text-center text-sm"
                              />
                              <span className="text-xs text-gray-400">
                                {product?.unit || item.unit}/pk
                              </span>
                            </div>
                          )}
                        </td>

                        <td className="px-2 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.mrp || ""}
                            onChange={(e) =>
                              updateItem(
                                index,
                                "mrp",
                                parseFloat(e.target.value) || 0,
                              )
                            }
                            className="w-20 px-1 py-1 border rounded text-center text-sm"
                          />
                          {(item.unitsPerPack || 1) > 1 &&
                            (item.mrp || 0) > 0 && (
                              <div className="text-[10px] text-gray-500 mt-0.5 text-center">
                                ₹
                                {(
                                  (item.mrp || 0) / (item.unitsPerPack || 1)
                                ).toFixed(2)}
                                /{product?.unit || "unit"}
                              </div>
                            )}
                        </td>

                        <td className="px-2 py-3">
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              updateItem(
                                index,
                                "quantity",
                                parseInt(e.target.value) || 1,
                              )
                            }
                            className="w-16 px-1 py-1 border rounded text-center"
                          />
                        </td>

                        <td className="px-2 py-3">
                          <input
                            type="number"
                            min="0"
                            value={item.freeQuantity || 0}
                            onChange={(e) =>
                              updateItem(
                                index,
                                "freeQuantity",
                                parseInt(e.target.value) || 0,
                              )
                            }
                            className="w-14 px-1 py-1 border rounded text-center text-sm"
                          />
                        </td>

                        <td className="px-2 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {(item.unitsPerPack || 1) > 1 && (
                              <Package className="w-3 h-3 text-blue-500" />
                            )}
                            <span
                              className={`text-sm font-medium ${(item.unitsPerPack || 1) > 1 ? "text-blue-700" : "text-gray-600"}`}
                            >
                              {item.baseQuantity}
                            </span>
                          </div>
                          {(item.freeQuantity || 0) > 0 && (
                            <div className="text-xs text-green-600">
                              +
                              {(item.freeQuantity || 0) *
                                (item.unitsPerPack || 1)}{" "}
                              free
                            </div>
                          )}
                        </td>

                        <td className="px-2 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unitPrice}
                            onChange={(e) =>
                              updateItem(
                                index,
                                "unitPrice",
                                parseFloat(e.target.value) || 0,
                              )
                            }
                            className="w-20 px-1 py-1 border rounded text-center"
                          />
                          {(item.unitsPerPack || 1) > 1 &&
                            item.unitPrice > 0 && (
                              <div className="text-[10px] text-blue-600 mt-0.5 text-center">
                                ₹
                                {(
                                  item.unitPrice / (item.unitsPerPack || 1)
                                ).toFixed(2)}
                                /{product?.unit || "unit"}
                              </div>
                            )}
                          {supplierPriceMeta && (
                            <div
                              className={`text-[10px] mt-0.5 text-center ${supplierPriceMeta.isAlert ? "text-red-600 font-semibold" : "text-gray-500"}`}
                            >
                              Contract ₹
                              {supplierPriceMeta.contractedPackPrice.toFixed(2)}
                              {supplierPriceMeta.deviationPercent >= 0
                                ? ` · ${supplierPriceMeta.deviationPercent.toFixed(1)}% vs contract`
                                : ` · ${Math.abs(supplierPriceMeta.deviationPercent).toFixed(1)}% below contract`}
                            </div>
                          )}
                        </td>

                        <td className="px-2 py-3">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={item.discountPercent}
                            onChange={(e) =>
                              updateItem(
                                index,
                                "discountPercent",
                                parseFloat(e.target.value) || 0,
                              )
                            }
                            className="w-16 px-1 py-1 border rounded text-center text-sm"
                          />
                        </td>

                        <td className="px-2 py-3">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={item.schemeDiscountPercent || 0}
                            onChange={(e) =>
                              updateItem(
                                index,
                                "schemeDiscountPercent",
                                parseFloat(e.target.value) || 0,
                              )
                            }
                            className="w-16 px-1 py-1 border rounded text-center text-sm"
                          />
                        </td>

                        <td className="px-2 py-3 text-right text-sm text-gray-900">
                          ₹{(item.taxableAmount || 0).toFixed(2)}
                        </td>

                        <td className="px-2 py-3">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={item.taxPercent}
                            onChange={(e) =>
                              updateItem(
                                index,
                                "taxPercent",
                                parseFloat(e.target.value) || 0,
                              )
                            }
                            className="w-14 px-1 py-1 border rounded text-center text-sm"
                          />
                        </td>

                        <td className="px-2 py-3 text-right font-medium text-gray-900">
                          ₹{item.totalAmount.toFixed(2)}
                        </td>

                        <td className="px-2 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No products added yet.
            </div>
          )}

          {items.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <div className="flex flex-col items-end space-y-2">
                <div className="flex justify-between w-72">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium text-gray-900">
                    ₹{totals.subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between w-72">
                  <span className="text-gray-600">Trade Discount:</span>
                  <span className="font-medium text-green-600">
                    -₹{totals.tradeDiscountAmount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between w-72">
                  <span className="text-gray-600">Scheme Discount:</span>
                  <span className="font-medium text-green-600">
                    -₹{totals.schemeDiscountAmount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between w-72">
                  <span className="text-gray-600">Taxable Amount:</span>
                  <span className="font-medium text-gray-900">
                    ₹{totals.taxableAmount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between w-72">
                  <span className="text-gray-600">GST:</span>
                  <span className="font-medium text-gray-900">
                    ₹{totals.taxAmount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between w-72 text-lg font-bold border-t pt-2 text-gray-900">
                  <span>Net Amount:</span>
                  <span>₹{totals.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Additional Information
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Terms & Conditions
              </label>
              <textarea
                value={termsConditions}
                onChange={(e) => setTermsConditions(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate("/purchase-orders")}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || items.length === 0}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
